'use strict'
import {DAGraph} from '../../util/Graph.js';

export default class TableColumns {
    /**
     * @param {TableColumn[]} columns?
     */
    constructor(columns = []) {
        TableColumns._assertUniqueNames(columns);
        this._columns = columns.slice();
        this._graph = TableColumns._buildGraph(columns);
        TableColumns._checkMissingColumns(this._columns);
    }

    /** @param {TableColumn} column */
    add(column) {
        let colName = column.getDisplayName();
        if (this.hasName(colName))
            throw new ColumnValidationError('name', `Column [${colName}] already exists.`)
        try {
            this._graph = TableColumns._buildGraph(this._columns.concat(column));
        } catch (err) {
            throw new ColumnValidationError('expression', 'Expression creates cycles.');
        }
        this._columns.push(column);
        TableColumns._checkMissingColumns(this._columns);
    }

    /**
     * @param {string} columnName
     * @param {TableColumn} update
     */
    update(columnName, update) {
        const existing = this.findByName(columnName);
        if (!existing)
            throw new ColumnValidationError('name', `Column [${columnName}] does not exists`);
        let colName = update.getDisplayName();
        if (existing.getDisplayName() !== colName && this.hasName(colName))
            throw new ColumnValidationError('name', 'Such column already exists.');
        try {
            this._graph = TableColumns._buildGraph(this._columns.filter(c=>columnName !== c.getDisplayName()).concat(update));
        } catch (err) {
            throw new ColumnValidationError('expression', 'Expression creates cycles.');
        }
        const idx = this._columns.findIndex(c=>c.getDisplayName() === columnName);
        this._columns[idx] = update;
        TableColumns._checkMissingColumns(this._columns);
    }

    /** @param {string} columnName */
    remove(columnName) {
        this._columns = this._columns.filter(c=>c.getDisplayName() !== columnName);
        this._graph = TableColumns._buildGraph(this._columns);
        TableColumns._checkMissingColumns(this._columns);
    }

    hasName(name) {
        return this.findByName(name) !== undefined;
    }

    /**
     * @param {number} index
     * @returns {TableColumn}
     */
    getByIndex(index) {
        return this._columns[index];
    }

    /**
     * @return {string[]}
     */
    getNames() {
        return this._columns.map(c => c.getDisplayName())
    }

    /** @returns {TableColumn[]} */
    getColumns(){
        return this._columns;
    }

    /**
     * @param {string[]} names
     * @return {(string|null)[]}
     */
    getTypes(names) {
        return names.map(name => {
            let column = this.findByName(name);
            return column ? column.getType() : null
        })
    }

    /**
     * @param {string} columnName
     * @returns {TableColumn}
     */
    findByName(columnName) {
        return this._columns.find(c=>c.getDisplayName() === columnName);
    }

    /**
     * @param {string[]} columnNames
     * @return {{indices: number[], names: string[], cols: TableColumn[]}}
     */
    getIndexedColumns(columnNames) {
        const result = {
            indices: [], cols: [], names: []
        }
        for (let i = 0; i < columnNames.length; i++) {
            result.names[i] = columnNames[i];
            result.cols[i] = this.findByName(columnNames[i])
            result.indices[i] = this._columns.findIndex(col=>col.getDisplayName() === columnNames[i])
        }
        return result;
    }

    /**
     * @param {number} index
     * @param {number} newIndex
     */
    moveColumn(index, newIndex) {
        const column = this._columns[index];
        this._columns.splice(index, 1);
        this._columns.splice(newIndex, 0, column);
    }

    /**
     * @param {string} columnName
     * @return {string[]}
     */
    getColumnsDependsOn(columnName) {
        const result = [];
        let vertex = this._graph.getVertex(columnName);
        if (vertex === undefined)
            return result;
        const queue = [vertex];
        while (queue.length > 0) {
            const current = queue.shift();
            if (this.hasName(current.obj))
                result.push(current.obj);
            for (const edge of current.edges)
                if (edge.to === current)
                    queue.push(edge.from)
        }
        return result;
    }

    length() {
        return this._columns.length;
    }

    toServerJson(){
        return this._columns.map(col=>col.toServerJson())
    }

    /** @return {IterableIterator<TableColumn>} */
    [Symbol.iterator]() {
        return this._columns.values();
    }

    /**
     * @param {TableColumn[]} columns
     * @returns {DAGraph}
     * @private
     */
    static _buildGraph(columns) {
        let graph = new DAGraph();
        for (const column of columns) {
            let colName = column.getDisplayName();
            graph.putVertex(colName);
            for (const relatedColumnName of column.getRelatedColumns()) {
                graph.addEdge(colName, relatedColumnName)
            }
        }
        return graph;
    }

    /**
     * @param {TableColumn[]} columns
     * @private
     */
    static _checkMissingColumns(columns) {
        for (const column of columns) {
            column.setMissingDependencies([]);
            for (const relatedColumnName of column.getRelatedColumns())
                if (columns.findIndex(c => c.getDisplayName() === relatedColumnName) === -1)
                    column.setMissingDependencies(column.getMissingDependencies().concat(relatedColumnName));
        }
    }

    static _assertUniqueNames(columns) {
        if (!(new Set(columns.map(c => c.getDisplayName())).size === columns.length))
            throw new Error("Column names must be unique");
    }
}

export class ColumnValidationError extends Error {
    /**
     * @param {string} field
     * @param {string} message
     */
    constructor(field, message) {
        super(message);
        this.field = field;
    }
}